import { LucideIcon, Lock } from "lucide-react";
import { Link } from "react-router-dom";

interface ActionCardProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  href?: string;
  disabled?: boolean;
  /** When set, renders the card in a locked/disabled state with this message */
  disabledReason?: string;
  /** Shows a skeleton pulse while role/permissions are loading */
  isLoading?: boolean;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "disabled";
}

export default function ActionCard({
  icon: Icon,
  title,
  description,
  href,
  disabled = false,
  disabledReason,
  isLoading = false,
  onClick,
  variant = "primary",
}: ActionCardProps) {

  if (isLoading) {
    return (
      <div className="relative w-full rounded-xl overflow-hidden p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95">
        <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-xl p-6 animate-pulse">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-slate-700/40 shrink-0" />
            <div className="flex-1 space-y-2.5 min-w-0 pt-1">
              <div className="h-5 bg-slate-700/40 rounded-md w-2/3" />
              <div className="h-4 bg-slate-700/25 rounded-md w-full" />
              <div className="h-4 bg-slate-700/25 rounded-md w-4/5" />
            </div>
          </div>
          <div className="mt-5 h-px bg-slate-700/20 rounded" />
          <div className="mt-3 h-3 bg-slate-700/20 rounded-md w-1/2" />
        </div>
      </div>
    );
  }

  const isDisabled = disabled || !!disabledReason;

  const variantClasses = {
    primary: "p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-[#3C6AC6] hover:via-[#D216CC] hover:to-[#FB6B96] hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1",
    secondary: "p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-[#3C6AC6] hover:via-[#D216CC] hover:to-[#FB6B96] hover:shadow-lg hover:shadow-cyan-500/20 hover:-translate-y-1",
    disabled: "p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 opacity-60 cursor-not-allowed",
  };

  const effectiveVariant = disabledReason ? "disabled" : variant;

  const iconClasses = {
    primary: "text-blue-400 group-hover:text-blue-300",
    secondary: "text-cyan-400 group-hover:text-cyan-300",
    disabled: "text-slate-500",
  };

  const titleClasses = {
    primary: "text-white group-hover:text-blue-100",
    secondary: "text-white group-hover:text-cyan-100",
    disabled: "text-slate-400",
  };

  const content = (
    <div
      className={`group relative w-full h-full rounded-xl transition-all duration-300 overflow-hidden ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} ${variantClasses[effectiveVariant]}`}
      onClick={isDisabled ? undefined : onClick}
    >
      <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-xl p-6 flex flex-col">
        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Main content row */}
        <div className="relative flex items-start gap-4 flex-1">
          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 ${
            effectiveVariant === "disabled"
              ? "bg-gray-800/30"
              : "bg-gradient-to-br from-gray-800/50 to-gray-900/60 group-hover:from-blue-500/20 group-hover:to-blue-600/20"
          }`}>
            <Icon className={`w-6 h-6 ${iconClasses[effectiveVariant]}`} />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className={`text-xl font-semibold mb-1 transition-colors duration-300 ${titleClasses[effectiveVariant]}`}>
              {title}
            </h3>
            {description && (
              <p className={`text-base transition-colors duration-300 ${
                effectiveVariant === "disabled" ? "text-slate-500" : "text-slate-400 group-hover:text-slate-300"
              }`}>
                {description}
              </p>
            )}
          </div>

          {/* Arrow or lock indicator */}
          {disabledReason ? (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-700/30 flex items-center justify-center">
              <Lock className="w-3.5 h-3.5 text-slate-500" />
            </div>
          ) : !isDisabled ? (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-800/50 flex items-center justify-center group-hover:bg-blue-500/20 transition-all duration-300">
              <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          ) : null}
        </div>

        {/* Disabled reason footer */}
        {disabledReason && (
          <div className="relative mt-4 flex items-center gap-1.5 border-t border-slate-700/20 pt-3">
            <Lock className="w-3 h-3 text-slate-600 shrink-0" />
            <span className="text-xs text-slate-600">{disabledReason}</span>
          </div>
        )}
      </div>
    </div>
  );

  if (href && !isDisabled) {
    return <Link to={href} className="block h-full">{content}</Link>;
  }

  return content;
}
